import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    SearchLg,
    Eye,
    CheckCircle,
    XCircle,
    Edit01
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Table, TableCard } from '@/components/application/table/table';
import { DialogTrigger, Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { orderService } from '@/data/mock-orders';
import { accountService } from '@/data/mock-accounts';
import { activityService } from '@/data/mock-activities';
import { shipperQuoteService } from '@/data/mock-quotes';
import { addressBookService } from '@/data/mock-address-book';
import { getWeekRange } from '@/utils/week-utils';
import type { Order, OrderStatus } from '@/types/order';
import { useTranslation } from 'react-i18next';
import { storage } from '@/lib/local-storage';

export const ShipperOrdersPage = () => {
    const { t } = useTranslation(['pages:orders', 'common']);
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isCreatingQuotes, setIsCreatingQuotes] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Pending', label: t('status.pending', { ns: 'types' }) },
        { id: 'Accepted', label: t('status.accepted', { ns: 'types' }) },
        { id: 'Rejected', label: t('status.rejected', { ns: 'types' }) },
        { id: 'Modified', label: t('status.modified', { ns: 'types' }) },
        { id: 'Confirmed', label: t('status.confirmed', { ns: 'types' }) },
        { id: 'Cancelled', label: t('status.cancelled', { ns: 'types' }) }
    ];

    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Accepted': return 'success';
            case 'Rejected': return 'error';
            case 'Modified': return 'warning';
            case 'Confirmed': return 'success';
            case 'Cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: OrderStatus) => {
        return t(`status.${status.toLowerCase()}`, { ns: 'types' });
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = storage.getItem('accountId') || '1';

            // Load orders for this sender
            const senderOrders = orderService.getByShipperId(accountId);
            setOrders(senderOrders);

            setIsLoading(false);
        };
        loadData();
    }, []);

    // Filter orders
    const filteredOrders = useMemo(() => {
        let filtered = [...orders];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order =>
                order.notes?.toLowerCase().includes(term) ||
                order.packingList.requested?.toLowerCase().includes(term) ||
                order.contractualInfo?.contractNumber?.toLowerCase().includes(term)
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(order => order.status === statusFilter);
        }

        return filtered;
    }, [orders, searchTerm, statusFilter]);

    const handleQuickAction = async (orderId: string, action: 'accept' | 'reject') => {
        if (action === 'accept') {
            setSelectedOrderId(orderId);
            setShowConfirmDialog(true);
        } else if (action === 'reject') {
            // Navigate to detail page for rejection (needs reason)
            navigate(`/shipper/orders/${orderId}?action=reject`);
        }
    };

    const handleAcceptWithoutQuotes = async () => {
        if (!selectedOrderId) return;

        const order = orderService.getById(selectedOrderId);
        if (!order) return;

        orderService.update(selectedOrderId, { status: 'Accepted' });

        // Create activity for receiver
        const receiverAccount = accountService.getById(order.receiverId);
        if (receiverAccount) {
            activityService.add({
                id: `order-${selectedOrderId}-accepted`,
                audience: 'receiver',
                type: 'status_update',
                title: `Ordine #${selectedOrderId} accettato`,
                description: `Il mittente ha accettato il tuo ordine per la settimana ${order.targetWeek}/${order.targetYear}.`,
                timestamp: new Date(),
                isRead: false,
                actions: [
                    { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${selectedOrderId}` }
                ],
                metadata: {
                    orderId: selectedOrderId,
                    receiverId: order.receiverId,
                    shipperId: order.shipperId
                }
            });
        }

        setShowConfirmDialog(false);
        setSelectedOrderId(null);

        // Reload orders
        const accountId = storage.getItem('accountId') || '1';
        const senderOrders = orderService.getByShipperId(accountId);
        setOrders(senderOrders);
    };

    const handleAcceptWithQuotes = async () => {
        if (!selectedOrderId) return;

        const order = orderService.getById(selectedOrderId);
        if (!order) return;

        setShowConfirmDialog(false);
        setIsCreatingQuotes(true);

        try {
            // Accept the order first
            orderService.update(selectedOrderId, { status: 'Accepted' });

            // Create activity for receiver
            const receiverAccount = accountService.getById(order.receiverId);
            if (receiverAccount) {
                activityService.add({
                    id: `order-${selectedOrderId}-accepted`,
                    audience: 'receiver',
                    type: 'status_update',
                    title: `Ordine #${selectedOrderId} accettato`,
                    description: `Il mittente ha accettato il tuo ordine per la settimana ${order.targetWeek}/${order.targetYear}.`,
                    timestamp: new Date(),
                    isRead: false,
                    actions: [
                        { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${selectedOrderId}` }
                    ],
                    metadata: {
                        orderId: selectedOrderId,
                        receiverId: order.receiverId,
                        shipperId: order.shipperId
                    }
                });
            }

            // Get shipper ID
            const shipperId = storage.getItem('accountId') || order.shipperId;

            // Get all freight forwarders from address book
            const freightForwarders = addressBookService.search({
                accountId: shipperId,
                role: 'Freight Forwarder',
            });

            if (freightForwarders.length === 0) {
                // No freight forwarders available, just accept the order
                setIsCreatingQuotes(false);
                setSelectedOrderId(null);

                // Reload orders
                const accountId = storage.getItem('accountId') || '1';
                const senderOrders = orderService.getByShipperId(accountId);
                setOrders(senderOrders);
                return;
            }

            // Calculate dates from week number
            const weekRange = getWeekRange(order.targetYear, order.targetWeek);
            const pickupDate = weekRange.start;
            const deliveryDate = new Date(weekRange.end.getTime() + 7 * 24 * 60 * 60 * 1000); // End of week + 7 days

            // Extract product information from order
            let product = '';
            let quantity: number | undefined;
            let unit = '';

            if (order.orderItems && order.orderItems.length > 0) {
                const firstItem = order.orderItems[0];
                product = firstItem.description;
                quantity = order.orderItems.reduce((sum, item) => sum + item.quantity, 0);
                unit = firstItem.unit;
            } else if (order.packingList.requested) {
                // Try to extract from packing list text
                product = order.packingList.requested.substring(0, 100);
            }

            // Build body text from order data
            const bodyParts: string[] = [];
            bodyParts.push(`Richiesta preventivo per ordine #${order.id}`);
            bodyParts.push('');

            if (order.packingList.requested) {
                bodyParts.push('Dettagli merce:');
                bodyParts.push(order.packingList.requested);
                bodyParts.push('');
            }

            if (order.orderItems && order.orderItems.length > 0) {
                bodyParts.push('Articoli ordine:');
                order.orderItems.forEach(item => {
                    bodyParts.push(`- ${item.description}: ${item.quantity} ${item.unit}`);
                    if (item.notes) {
                        bodyParts.push(`  Note: ${item.notes}`);
                    }
                });
                bodyParts.push('');
            }

            bodyParts.push(`Settimana target: ${order.targetWeek}/${order.targetYear}`);
            bodyParts.push(`Data ritiro prevista: ${pickupDate.toLocaleDateString('it-IT')}`);
            bodyParts.push(`Data consegna prevista: ${deliveryDate.toLocaleDateString('it-IT')}`);

            if (order.contractualInfo) {
                bodyParts.push('');
                bodyParts.push('Informazioni contrattuali:');
                if (order.contractualInfo.contractNumber) {
                    bodyParts.push(`Numero contratto: ${order.contractualInfo.contractNumber}`);
                }
                if (order.contractualInfo.terms) {
                    bodyParts.push(`Termini: ${order.contractualInfo.terms}`);
                }
            }

            if (order.notes) {
                bodyParts.push('');
                bodyParts.push('Note aggiuntive:');
                bodyParts.push(order.notes);
            }

            const body = bodyParts.join('\n');

            // Create extracted data
            const extractedData = {
                product: product || undefined,
                quantity: quantity,
                unit: unit || undefined,
                pickupDate: pickupDate,
                deliveryDate: deliveryDate,
                ...(order.contractualInfo?.terms && { specialRequirements: [order.contractualInfo.terms] }),
            };

            // Create quote request
            const newQuoteRequest = shipperQuoteService.create({
                subject: `Richiesta preventivo per ordine #${order.id}`,
                body: body,
                recipients: freightForwarders.map(ff => ff.id),
                extractedData: extractedData,
                shipperId: shipperId,
                orderId: order.id,
            });

            // Simulate some delay for better UX
            await new Promise(resolve => setTimeout(resolve, 1500));

            setIsCreatingQuotes(false);
            setSelectedOrderId(null);
            navigate(`/shipper/quotes/${newQuoteRequest.id}`);
        } catch (error) {
            console.error('Error creating quote request:', error);
            setIsCreatingQuotes(false);
            setSelectedOrderId(null);

            // Reload orders
            const accountId = storage.getItem('accountId') || '1';
            const senderOrders = orderService.getByShipperId(accountId);
            setOrders(senderOrders);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('sender.title')}</h1>
                    <p className="text-tertiary mt-1">{t('sender.subtitle')}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />
                </div>
                <div className="w-full sm:w-64">
                    <Select
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as OrderStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Orders Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('sender.tableTitle')}
                    description={t('sender.tableDescription', { count: filteredOrders.length })}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('sender.headers.orderId')} isRowHeader />
                        <Table.Head label={t('sender.headers.receiver')} />
                        <Table.Head label={t('sender.headers.targetWeek')} />
                        <Table.Head label={t('sender.headers.status')} />
                        <Table.Head label={t('sender.headers.createdAt')} />
                        <Table.Head label={t('sender.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredOrders.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={6} className="text-center py-8">
                                    <p className="text-tertiary">{t('list.noOrders')}</p>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            filteredOrders.map((order) => {
                                const receiverAccount = accountService.getById(order.receiverId);

                                return (
                                    <Table.Row key={order.id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">#{order.id}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                {receiverAccount?.legalName || order.receiverId}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                Settimana {order.targetWeek}/{order.targetYear}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={getStatusColor(order.status)} size="sm">
                                                {translateStatus(order.status)}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {order.createdAt.toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                {order.status === 'Pending' && (
                                                    <>
                                                        <Button
                                                            onClick={() => handleQuickAction(order.id, 'accept')}
                                                            color="primary"
                                                            size="sm"
                                                            iconLeading={CheckCircle}
                                                        >
                                                            {t('sender.accept')}
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleQuickAction(order.id, 'reject')}
                                                            color="primary-destructive"
                                                            size="sm"
                                                            iconLeading={XCircle}
                                                        >
                                                            {t('sender.reject')}
                                                        </Button>
                                                        <Button
                                                            onClick={() => navigate(`/shipper/orders/${order.id}?action=modify`)}
                                                            color="secondary"
                                                            size="sm"
                                                            iconLeading={Edit01}
                                                        >
                                                            {t('sender.modify')}
                                                        </Button>
                                                    </>
                                                )}
                                                <Button
                                                    onClick={() => navigate(`/shipper/orders/${order.id}`)}
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={Eye}
                                                >
                                                    {t('buttons.view', { ns: 'common' })}
                                                </Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })
                        )}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* Confirm Create Quotes Dialog */}
            <DialogTrigger isOpen={showConfirmDialog} onOpenChange={(open) => !open && setShowConfirmDialog(false)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('orderAccept.confirmCreateQuotes.title', { ns: 'common' })}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('orderAccept.confirmCreateQuotes.message', { ns: 'common' })}
                                </p>
                                <div className="flex justify-between gap-3">
                                    <Button
                                        onClick={() => setShowConfirmDialog(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('buttons.cancel', { ns: 'common' })}
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={handleAcceptWithoutQuotes}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('orderAccept.confirmCreateQuotes.acceptOnly', { ns: 'common' })}
                                        </Button>
                                        <Button
                                            onClick={handleAcceptWithQuotes}
                                            color="primary"
                                            size="md"
                                        >
                                            {t('orderAccept.confirmCreateQuotes.createQuotes', { ns: 'common' })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Creating Quotes Loading Modal */}
            <ModalOverlay isOpen={isCreatingQuotes} onOpenChange={() => { }}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
                            <div className="flex flex-col items-center gap-4">
                                <LoadingIndicator type="line-spinner" size="lg" />
                                <p className="text-lg font-semibold text-primary">
                                    {t('orderAccept.creatingQuotes', { ns: 'common' })}
                                </p>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

